import {
  type V2AdminClientDto,
  type V2AdminClientLookupDto,
  type V2AdminClientsPageDto,
  type V2AdminClientsSummaryDto,
  v2AdminClientsControllerCreate,
  v2AdminClientsControllerDetail,
  v2AdminClientsControllerList,
  v2AdminClientsControllerLookup,
  v2AdminClientsControllerPrint,
  v2AdminClientsControllerRemove,
  v2AdminClientsControllerSummary,
  v2AdminClientsControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminClientLookupSchema,
  V2AdminClientStoredSchema,
  V2AdminClientsPageSchema,
  V2AdminClientsSummarySchema
} from '@idc-forms/schemas/api-v2/admin-clients'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Clientes contra la API v2 (ADR 0019/0020). Consume el SDK generado con el
 * Bearer de la sesión staff v2, pagina por cursor y usa el contrato de consulta
 * rico. Identifica todo por id público (`cli_`); el asesor viaja como `usr_`,
 * nunca como ObjectId.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer`
 * con bracket notation. El serializer por defecto del SDK usa `deepObject` de UN
 * solo nivel y explota en runtime ante cualquier filtro anidado del ADR 0020.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type ClientsAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

/** Parámetros del selector: es un desplegable, no un listado paginado. */
export type ClientsV2LookupQuery = {
  search?: string
  typePerson?: 'individual' | 'juridical'
  /** Códigos de cliente, para traer varios de una (el legacy hacía N consultas). */
  codes?: number[]
  /** `cli_` a dejar afuera — típicamente los dueños ya elegidos. */
  exclude?: string[]
}

export type ClientsAdminV2Row = V2AdminClientsPageDto['data'][number]
export type ClientV2Dossier = V2AdminClientDto
export type ClientV2LookupItem = V2AdminClientLookupDto['data'][number]

function toListQuery(query: ClientsAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const clientsAdminV2Service = {
  list: async (accessToken: string, query: ClientsAdminV2Query): Promise<V2AdminClientsPageDto> => {
    const result = await v2AdminClientsControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminClientsPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<ClientsAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminClientsSummaryDto> => {
    const result = await v2AdminClientsControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminClientsSummarySchema, result.data)
  },

  /**
   * Selector de clientes. Un solo caso de uso reemplaza a los tres del legacy
   * —el selector de negociaciones, el traer-varios-por-código y la búsqueda de
   * invitaciones—, y proyecta: el legacy pegaba el expediente FEIC completo de
   * cada cliente para llenar un desplegable de seis campos.
   */
  lookup: async (
    accessToken: string,
    query: ClientsV2LookupQuery = {}
  ): Promise<ClientV2LookupItem[]> => {
    const result = await v2AdminClientsControllerLookup({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: {
        ...(query.search ? { search: query.search } : {}),
        ...(query.typePerson ? { typePerson: query.typePerson } : {}),
        ...(query.codes?.length ? { codes: query.codes.join(',') } : {}),
        ...(query.exclude?.length ? { exclude: query.exclude.join(',') } : {})
      }
    })
    return assertShape(V2AdminClientLookupSchema, result.data).data
  },

  detail: async (accessToken: string, id: string): Promise<ClientV2Dossier> => {
    const result = await v2AdminClientsControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminClientStoredSchema, result.data)
  },

  /**
   * Expediente para generación de documentos. Es OTRA autorización que la
   * lectura —la gobiernan los permisos de impresión— y no aplica el alcance del
   * asesor: quien imprime una promesa necesita los datos del cliente de la
   * negociación aunque no sea su asesor.
   */
  print: async (accessToken: string, id: string): Promise<ClientV2Dossier> => {
    const result = await v2AdminClientsControllerPrint({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminClientStoredSchema, result.data)
  },

  /**
   * Borrado lógico del expediente. Devuelve 204 y no un cuerpo: no hay recurso
   * que describir después de borrarlo.
   *
   * Errores propios: 409 `CLIENT_HAS_NEGOTIATIONS` si el cliente pertenece a
   * alguna negociación y 403 `NOT_AVAILABLE_IN_PRODUCTION`, porque borrar un
   * expediente sigue siendo una herramienta de dev/QA como en v1.
   */
  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminClientsControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  },

  /**
   * Alta del expediente. Devuelve el expediente, no un eco del payload: el FEIC
   * navega al recién creado con el `cli_` que sale de acá.
   *
   * Errores propios: 409 `CLIENT_DUPLICATE` —con el `cli_` del que ya existe, que
   * es lo que el manejador de errores usa para ofrecer «ver cliente»— y 400
   * `USER_IS_NOT_ADVISOR` si el asesor elegido no lo es.
   */
  create: async (accessToken: string, input: unknown): Promise<ClientV2Dossier> => {
    const result = await v2AdminClientsControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body: input as never
    })
    return result.data
  },

  /**
   * Edición por MERGE DE SECCIÓN: la que no viaja no se toca. Con un flujo del
   * portal abierto responde 409 `CLIENT_HAS_ACTIVE_FLOW` — la pelota es del
   * cliente hasta que ese flujo cierre.
   */
  update: async (accessToken: string, id: string, input: unknown): Promise<ClientV2Dossier> => {
    const result = await v2AdminClientsControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: input as never
    })
    return result.data
  }
}
